// ContactKundentagebuchTab — der Reiter „Kundentagebuch" der Kontaktdetails.
//
// Führt zusammen, was vorher auf zwei Reiter verteilt war: die Dauernotiz und
// die Vorgänge (bisher Reiter „Kundentagebuch") sowie die Verlaufs-Timeline
// (bisher Reiter „Archiv"). Drei Klapp-Sektionen, damit die Chronik eines
// Kunden an EINER Stelle steht, ohne die Seite zu überladen.
//
// Die Vorgänge-Karte zeigt seit 09.09.2026 dieselbe Tabelle wie der Reiter
// „Vorgänge" (`KontaktVorgaengeReiter`, Variante „eingebettet") — inklusive der
// indirekten Vorgänge aus Firma, Verbund und Haushalt. Zwei verschiedene
// Vorgangs-Ansichten in derselben Akte waren eine zu viel.
//
// Seit PO 14.09.2026 sind die Notizen getrennt: „Dauernotiz" oben trägt nur
// noch die eigenen Notizen des Kunden, die aus Vorgängen und To-dos stehen im
// „Verlauf / Archiv" darunter (`mitNotizen`) — in DEM Archiv, nicht in einem
// zweiten daneben.
//
// ══ DAMIT DIE TRENNUNG NICHT WIE EIN VERLUST AUSSIEHT ══════════════════════
// Auf Bestandskunden gibt es oft KEINE eigene Notiz: alles Geschriebene hängt
// an einem Vorgang oder To-do. Die Fläche oben war damit leer, und das Archiv
// darunter schwieg — es lädt faul und meldet seinen Zähler erst nach dem
// Aufklappen. Ein leerer Reiter neben einer stummen Karte, in der hunderte
// Notizen liegen.
//
// Zwei Handgriffe, beide OHNE einen zweiten Abruf:
//  1. Die leere Fläche nennt die Zahl der fremden Notizen und verweist ins
//     Archiv; der Verweis klappt es auf (`onArchivOeffnen` → `openSignal`).
//  2. Der Archiv-Kopf trägt diese Zahl AUCH ZUGEKLAPPT als Zusatz
//     („610 Notizen"). Sie kommt von der Notizfläche, die ihre Liste ohnehin
//     lädt — die fremden Notizen liegen dort schon im Speicher. Das Archiv
//     selbst bleibt faul: sein eigener Zähler (alle zehn Arten) erscheint
//     weiterhin erst nach dem Aufklappen, und weil der Zusatz beschriftet ist
//     („Notizen"), wird er auch nicht mit der Gesamtzahl verwechselt.
// ═══════════════════════════════════════════════════════════════════════════

import * as React from "react";
import { useCallback, useRef, useState } from "react";
import { CollapsibleCard } from "../vorgang/detail/CollapsibleCard";
import {
    KontaktVorgaengeReiter,
    type KontaktVorgaengeHandle,
} from "../kontaktVorgaenge/KontaktVorgaengeReiter";
import ContactArchiv from "../ContactArchiv";
import {
    KundentagebuchNotizen,
    type KundentagebuchNotizenHandle,
} from "./KundentagebuchNotizen";

type Props = { contactId: number };

/** Knopf im Sektionskopf — bewusst schlicht, die Karte trägt die Optik. */
const KopfBtn: React.FC<{
    onClick: () => void;
    title?: string;
    primary?: boolean;
    children: React.ReactNode;
}> = ({ onClick, title, primary = false, children }) => (
    <button
        type="button"
        title={title}
        onClick={onClick}
        style={{
            padding: primary ? "6px 14px" : "5px 10px",
            borderRadius: 999,
            border: `1px solid ${primary ? "#4d2051" : "#e3e0e7"}`,
            background: primary ? "#4d2051" : "#fff",
            color: primary ? "#fff" : "#2d142b",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: primary ? 700 : 500,
            whiteSpace: "nowrap",
        }}
    >
        {children}
    </button>
);

export const ContactKundentagebuchTab: React.FC<Props> = ({ contactId }) => {
    const notesRef = useRef<KundentagebuchNotizenHandle>(null);
    const vorgaengeRef = useRef<KontaktVorgaengeHandle>(null);
    /** Ziel des Verweises aus der leeren Notizfläche (Scroll-Anker). */
    const archivRef = useRef<HTMLDivElement>(null);

    const [notizCount, setNotizCount] = useState<number | null>(null);
    const [vorgangCount, setVorgangCount] = useState<number | null>(null);
    const [archivCount, setArchivCount] = useState<number | null>(null);
    /** Notizen aus Vorgängen und To-dos — die Vorab-Zahl der Archiv-Karte. */
    const [archivNotizCount, setArchivNotizCount] = useState<number | null>(null);
    /**
     * Zählwerk für `openSignal`: jede Erhöhung klappt die Archiv-Karte auf.
     * Start `null`, damit der Effekt in `CollapsibleCard` beim ersten Rendern
     * nichts tut — die Karte soll zugeklappt beginnen.
     */
    const [archivSignal, setArchivSignal] = useState<number | null>(null);

    // Stabile Referenzen: die Kinder melden ihren Zähler aus einem useEffect,
    // eine je Render neue Funktion würde ihn in einer Schleife auslösen.
    const onNotizCount = useCallback((n: number) => setNotizCount(n), []);
    const onVorgangCount = useCallback((n: number) => setVorgangCount(n), []);
    const onArchivCount = useCallback((n: number) => setArchivCount(n), []);
    const onArchivNotizCount = useCallback((n: number) => setArchivNotizCount(n), []);

    /**
     * Der Verweis aus der leeren Notizfläche: Archiv aufklappen und hinscrollen
     * — es steht ganz unten, sonst klappte etwas außerhalb des Bildes auf.
     * `scrollIntoView` wird optional gerufen: im Test-DOM (jsdom) gibt es die
     * Methode nicht, und dafür soll der Klick nicht durchfallen.
     */
    const archivAufklappen = useCallback(() => {
        setArchivSignal((n) => (n ?? 0) + 1);
        requestAnimationFrame(() =>
            archivRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }),
        );
    }, []);

    /**
     * Der Zusatz am Archiv-Kopf. Beschriftet, nicht bloß eine Zahl: neben dem
     * Zähler der Karte (alle Arten, erst nach dem Laden) muss erkennbar
     * bleiben, dass hier NUR die Notizen gemeint sind.
     */
    const archivNotizZusatz =
        archivNotizCount != null && archivNotizCount > 0 ? (
            <span title="Notizen aus Vorgängen und To-dos">
                {archivNotizCount === 1 ? "1 Notiz" : `${archivNotizCount} Notizen`}
            </span>
        ) : undefined;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <CollapsibleCard
                title="Dauernotiz"
                count={notizCount}
                actions={
                    <KopfBtn onClick={() => notesRef.current?.openNewNote()}>
                        + Neue Notiz
                    </KopfBtn>
                }
            >
                <KundentagebuchNotizen
                    ref={notesRef}
                    contactId={contactId}
                    onCountChange={onNotizCount}
                    onFremdeCountChange={onArchivNotizCount}
                    onArchivOeffnen={archivAufklappen}
                    headerless
                />
            </CollapsibleCard>

            <CollapsibleCard
                title="Vorgänge"
                count={vorgangCount}
                actions={
                    <span style={{ display: "flex", gap: 8 }}>
                        <KopfBtn
                            onClick={() => vorgaengeRef.current?.neuLaden()}
                            title="Aktualisieren"
                        >
                            ↻
                        </KopfBtn>
                        <KopfBtn primary onClick={() => vorgaengeRef.current?.neuerVorgang()}>
                            + Neuer Vorgang
                        </KopfBtn>
                    </span>
                }
            >
                <KontaktVorgaengeReiter
                    ref={vorgaengeRef}
                    contactId={contactId}
                    variant="eingebettet"
                    onCountChange={onVorgangCount}
                />
            </CollapsibleCard>

            {/* Eingeklappt: CollapsibleCard hängt den Body aus dem Baum, das
                Archiv lädt seine Quellen also erst beim Aufklappen — auch die
                Notizen, die es sich mit `mitNotizen` selbst holt. Die Faulheit
                bleibt; nur die Zahl der Notizen steht schon vorher am Kopf
                (`zusatz`), damit die Karte nicht stumm über hunderten Einträgen
                sitzt. Der Umweg über die Notizfläche ist der Punkt: sie hat die
                Liste bereits. */}
            <div ref={archivRef}>
                <CollapsibleCard
                    title="Verlauf / Archiv"
                    count={archivCount}
                    zusatz={archivNotizZusatz}
                    openSignal={archivSignal}
                    defaultCollapsed
                >
                    <ContactArchiv
                        contactId={contactId}
                        embedded
                        onCountChange={onArchivCount}
                        mitNotizen
                    />
                </CollapsibleCard>
            </div>
        </div>
    );
};

export default ContactKundentagebuchTab;
